import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { RxCross2 } from "react-icons/rx";

import { CardCategoryEnum } from "@sorcery/db";
import { Button, H3, InternalLink, Line, P, VariantSwitcher } from "@sorcery/ui";

import { FAQList } from "~/components/molecules/FAQList";
import { CardGalleryImage } from "~/components/molecules/card/CardGalleryImage";
import { useModal } from "~/providers/ModalProvider";
import { fullCollectionlistJotai } from "~/state/collectionJotai";
import {
  avatarJotai,
  deckJotai,
  decklistJotai,
  sideboardJotai,
  trackingJotai,
} from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import { cardPath } from "~/utils/paths";
import { api } from "~/utils/trpc";
import { CollectionTrackingMessage } from "../atoms/CollectionTrackingMessage";
import { VariantLabel } from "../atoms/VariantLabel";
import { CardFlipper } from "../molecules/card/CardFlipper";

export const CardGallery = ({ card }: { card: CardType }) => {
  const { closeModal } = useModal();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const isSite = card.guardian!.category === CardCategoryEnum.Site;

  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const [variant, setVariant] = useState(card.variants[0]!);
  const hasReverse = !!card.reverse;
  const [isReversed, setIsReversed] = useState(false);

  /**
   * collection management
   */
  const tracking = useAtomValue(trackingJotai);
  const fullCollectionlist = useAtomValue(fullCollectionlistJotai);
  const itemsInCollection = useMemo(
    () =>
      tracking
        ? (fullCollectionlist
            ?.find((c) => c.card.id === card.id)
            ?.groupings.reduce((acc, grouping) => acc + grouping.items.length, 0) ?? 0)
        : undefined,
    [tracking, fullCollectionlist, card.id],
  );

  /**
   * deck management
   */
  const deck = useAtomValue(deckJotai);
  const avatar = useAtomValue(avatarJotai);
  const decklist = useAtomValue(decklistJotai);
  const sideboard = useAtomValue(sideboardJotai);
  const itemsInDeckAndSideboard = useMemo(
    () =>
      tracking && deck && avatar && decklist && sideboard
        ? (avatar.cardId === card.id ? avatar.quantity : 0) +
          (decklist.find((c) => c.cardId === card.id)?.quantity ?? 0) +
          (sideboard.find((c) => c.cardId === card.id)?.quantity ?? 0)
        : undefined,
    [tracking, deck, avatar, decklist, sideboard, card.id],
  );

  const toggleVariant = () => {
    setIsReversed(!isReversed);
  };

  const displayedVariant =
    isReversed && hasReverse
      ? // the no reverse on a reverse issue
        (variant.reverse as unknown as VariantType)
      : variant;

  const { data: faqs } = api.card.getFaqs.useQuery({ slug: card.slug });
  return (
    <div className="relative w-fit">
      {/**
       * headlessui focuses first focusable element,
       * so using a button here with outline-none is our hack to control that
       */}
      <button
        type="button"
        onClick={() => closeModal()}
        className={`absolute right-0 top-0 cursor-pointer text-3xl 
          text-white outline-none sm:-right-1 sm:-top-8 md:top-0`}
      >
        <RxCross2 />
      </button>

      {/* content */}
      <div className="flex flex-col items-center justify-center sm:flex-row sm:items-start">
        {/* image */}
        <div
          className="relative mb-2 hidden duration-500 animate-in fade-in sm:block"
          key={displayedVariant.id}
        >
          <CardGalleryImage variant={displayedVariant} rotate={isSite} />
          {/* reverse flipper */}
          {hasReverse && <CardFlipper onFlip={toggleVariant} />}
        </div>

        {/* right section */}
        <div className="gallery-breakpoints sm:ml-6">
          <div className="hidden sm:!block">
            <H3 className="!mb-2">{isReversed ? card.reverse?.name : card.name}</H3>
            <div className="duration-500 animate-in fade-in">
              {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
              <P className="!mb-2 italic">{displayedVariant.vMeta!.typeText}</P>
              <P className="curiosa-quote !mb-2 opacity-80">
                {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
                {displayedVariant.setCard.meta!.rulesText}
              </P>
              <P className="opacity-80">{displayedVariant.artist.name}</P>
            </div>
          </div>

          {/* sm screen */}
          <H3 className="text-center sm:hidden">{card.name}</H3>
          <div
            // biome-ignore lint/style/noUnusedTemplateLiteral: <explanation>
            className={`relative mb-8 flex flex-row justify-center duration-500 animate-in fade-in sm:mb-0 sm:hidden`}
            key={displayedVariant.id}
          >
            <CardGalleryImage variant={displayedVariant} rotate={isSite} />
            {/* reverse flipper */}
            {hasReverse && <CardFlipper onFlip={toggleVariant} />}
          </div>
          <Line className="my-4 !bg-line-color" />

          {/* switcher */}
          {card.variants.length > 1 ? (
            <VariantSwitcher
              variant={variant}
              variants={card.variants}
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              onChange={(v) => setVariant(card.variants.find((i) => i.id === v)!)}
            />
          ) : (
            <VariantLabel variant={displayedVariant} />
          )}

          {/* collection stuff */}
          <CollectionTrackingMessage
            itemsInCollection={itemsInCollection}
            itemsInDeckAndSideboard={itemsInDeckAndSideboard}
            className="pt-1"
          />

          {/* faqs */}
          {!!faqs?.length && (
            <div className="duration-500 animate-in fade-in">
              <Line className="my-4 !bg-line-color" />
              <div className="mb-4 max-h-[160px] overflow-y-scroll gradient-mask-b-90 md:max-h-[200px] lg:max-h-[300px] xl:max-h-[320px]">
                <P className="!mb-2 text-left font-bold">
                  {faqs.length}
                  <span>&nbsp;FAQ</span>
                  {faqs.length > 1 && <span>s</span>}
                </P>
                <FAQList variant="sm" faqs={faqs} className="opacity-80" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <Line className="my-4 !bg-line-color" />
      <div className="flex flex-row-reverse justify-between gap-2">
        <Button onClick={closeModal} variant="outline">
          Close
        </Button>
        <InternalLink
          href={cardPath(card.slug)}
          onClick={() => closeModal()}
          className="hover:text-primary cursor-pointer transition-colors"
        >
          Go to card
        </InternalLink>
      </div>
    </div>
  );
};
